import React, { useState, useMemo } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  CircularProgress,
  TableSortLabel,
  TablePagination,
  Box,
  Typography,
  Chip
} from '@mui/material';

const columns = [
  { label: 'Batch Number', key: 'batch_number', sortable: true },
  { label: 'Product', key: 'product', sortable: true },
  { label: 'HM Purity', key: 'hm_purity', sortable: true },
  { label: 'Qty', key: 'qty', sortable: true, numeric: true },
  { label: 'Net weight', key: 'net_weight', sortable: true, numeric: true },
  { label: 'Gross Wt', key: 'gross_weight', sortable: true, numeric: true },
  { label: 'Pure Weight', key: 'net_weight_purity', sortable: true, numeric: true },
  { label: 'BOM Dia Qty', key: 'bom_dia_qty', sortable: true, numeric: true },
  { label: 'BOM Clr Qty', key: 'bom_clr_qty', sortable: true, numeric: true },
  { label: 'Issue Tot Qty', key: 'issue_tot_qty', sortable: true, numeric: true },
  { label: 'Stn Wt/Gms', key: 'stone_weight', sortable: true, numeric: true },
  { label: 'Stn Wt/Cts', key: 'stone_weight_cts', sortable: true, numeric: true },
  { label: 'Department Name', key: 'department_name', sortable: true, highlight: true },
  { label: 'Worker Name', key: 'worker_name', sortable: true, highlight: true },
  { label: 'Received Weight', key: 'received_weight', sortable: true, numeric: true, highlight: true },
  { label: 'Issue Weight', key: 'issue_weight', sortable: true, numeric: true, highlight: true },
  { label: 'Net Weight', key: 'bip_net_weight', sortable: true, numeric: true, highlight: true },
  { label: 'Pure Weight', key: 'bip_pure_weight', sortable: true, numeric: true, highlight: true },
  { label: 'Removal Weight', key: 'removal_weight', sortable: true, numeric: true, highlight: true },
  { label: 'Stone Weight', key: 'bip_stone_weight', sortable: true, numeric: true, highlight: true },
  { label: 'Stone Qty', key: 'stone_qty', sortable: true, numeric: true, highlight: true },
  { label: 'Dia Qty', key: 'dia_qty', sortable: true, numeric: true, highlight: true },
  { label: 'Dia Broken', key: 'dia_broken', sortable: true, numeric: true, highlight: true },
  { label: 'Color Stone Qty', key: 'color_stone_qty', sortable: true, numeric: true, highlight: true },
  { label: 'Color Stone Broken', key: 'color_stone_broken', sortable: true, numeric: true, highlight: true },
  { label: 'Nick Plate Qty', key: 'nick_plate_qty', sortable: true, numeric: true, highlight: true },
  { label: 'Nick Plate Broken', key: 'nick_plate_broken', sortable: true, numeric: true, highlight: true },
  { label: 'Pearl Weight', key: 'pearl_weight', sortable: true, numeric: true, highlight: true },
  { label: 'Pearl Qty', key: 'pearl_qty', sortable: true, numeric: true, highlight: true },
  { label: 'Warehouse', key: 'warehouse', sortable: true },
  { label: 'JO No', key: 'jo_number', sortable: true },
  { label: 'JO Date', key: 'jo_date', sortable: true },
  { label: 'Prod Route', key: 'production_route', sortable: true },
  { label: 'Production Date', key: 'production_date', sortable: true },
  { label: 'Completion Date', key: 'completion_date', sortable: true },
  { label: 'Production Status', key: 'production_status', sortable: true },
  { label: 'Production Remarks', key: 'production_remarks', sortable: true },
  { label: 'Created By', key: 'created_by', sortable: true },
  { label: 'Created At', key: 'created_at', sortable: true },
];

const DepartmentWiseProductionReportTable = ({ reportData, loading }) => {
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('batch_number');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortedData = useMemo(() => {
    if (!reportData || reportData.length === 0) return [];
    
    return [...reportData].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      
      // Handle numeric values
      if (columns.find(col => col.key === orderBy)?.numeric) {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      } else {
        // Handle string values
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
      }
      
      if (order === 'desc') {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      } else {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      }
    });
  }, [reportData, order, orderBy]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return sortedData.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  const createSortHandler = (property) => (event) => {
    handleRequestSort(property);
  };

  const getStatusChip = (status) => {
    if (status === 'Completed') {
      return <Chip label="Completed" color="success" size="small" />;
    } else if (status === 'In Progress') {
      return <Chip label="In Progress" color="warning" size="small" />;
    } else {
      return <Chip label={status} color="default" size="small" />;
    }
  };

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer sx={{ maxHeight: 440 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.numeric ? 'right' : 'left'}
                  sortDirection={orderBy === col.key ? order : false}
                  sx={col.highlight ? { backgroundColor: '#e3f2fd' } : {}}
                >
                  {col.sortable ? (
                    <TableSortLabel
                      active={orderBy === col.key}
                      direction={orderBy === col.key ? order : 'asc'}
                      onClick={createSortHandler(col.key)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                    <Typography sx={{ ml: 2 }}>Loading...</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Typography sx={{ py: 2 }}>No data found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, idx) => (
                <TableRow key={idx}>
                  {columns.map((col) => (
                    <TableCell 
                      key={col.key} 
                      align={col.numeric ? 'right' : 'left'}
                      sx={col.highlight ? { backgroundColor: '#e3f2fd' } : {}}
                    >
                      {col.key === 'production_status'
                        ? getStatusChip(row[col.key])
                        : col.key === 'jo_date' || col.key === 'production_date' || col.key === 'completion_date'
                          ? (row[col.key] ? new Date(row[col.key]).toLocaleDateString() : '-')
                          : [
                              'stone_qty', 'dia_qty', 'dia_broken', 'color_stone_qty', 'color_stone_broken',
                              'nick_plate_qty', 'nick_plate_broken', 'pearl_weight', 'pearl_qty',
                              'received_weight', 'issue_weight', 'bip_net_weight', 'bip_pure_weight', 'removal_weight', 'bip_stone_weight'
                            ].includes(col.key)
                            ? (row[col.key] !== undefined && row[col.key] !== null ? parseFloat(row[col.key]).toFixed(3) : '-')
                            : col.numeric && row[col.key]
                              ? parseFloat(row[col.key]).toFixed(3)
                              : row[col.key] || '-'}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={reportData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Rows per page:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`}
      />
    </Paper>
  );
};

export default DepartmentWiseProductionReportTable; 